import { Box, Grid, Paper, Typography, Chip, makeStyles } from '@material-ui/core';
import ArrowForwardIcon from '@material-ui/icons/ArrowForward';
import LinearScaleIcon from '@material-ui/icons/LinearScale';
import NotesIcon from '@material-ui/icons/Notes';
import PersonIcon from '@material-ui/icons/Person';
import { Env, ServiceEntry, resolveDepOrder } from '../promoteUtils';
import { VersionMap } from './StepSelect';
import { NotesMode } from './StepNotes';

const useStyles = makeStyles((theme) => ({
  planRow: {
    display: 'grid',
    gridTemplateColumns: '32px 1fr auto auto',
    gap: theme.spacing(1.5),
    alignItems: 'center',
    padding: theme.spacing(1.25, 1.5),
    background:
      theme.palette.type === 'dark'
        ? 'rgba(255,255,255,0.02)'
        : 'rgba(0,0,0,0.02)',
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    marginBottom: theme.spacing(1),
  },
  planIndex: {
    fontFamily: 'monospace',
    fontSize: 11,
    color: theme.palette.text.secondary,
    textAlign: 'center',
  },
  serviceName: {
    fontFamily: 'monospace',
    fontWeight: 600,
    fontSize: 13,
  },
  versionArrow: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontFamily: 'monospace',
    fontSize: 12,
  },
  rail: {
    padding: theme.spacing(2),
  },
  railLabel: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    fontSize: 11,
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: 0.6,
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(1),
  },
}));

export interface StepConfirmProps {
  selectedServices: ServiceEntry[];
  versions: VersionMap;
  sourceEnv: Env;
  targetEnv: Env;
  hasNotes: boolean;
  notesMode: NotesMode;
  bundleVersion: string | null;
  owner: string | null;
}

export const StepConfirm = ({
  selectedServices,
  versions,
  sourceEnv,
  targetEnv,
  hasNotes,
  notesMode,
  bundleVersion,
  owner,
}: StepConfirmProps) => {
  const classes = useStyles();
  const selectedNames = new Set(selectedServices.map((s) => s.name));
  const order = resolveDepOrder(selectedServices, selectedNames);

  return (
    <Grid container spacing={2}>
      <Grid item xs={12} md={8}>
        <Paper variant="outlined" style={{ padding: 24 }}>
          <Typography
            variant="overline"
            color="textSecondary"
            style={{ letterSpacing: 0.8 }}
          >
            Ready to promote
          </Typography>
          <Typography variant="h5" style={{ fontWeight: 700, marginTop: 4 }}>
            {selectedServices.length} services · {sourceEnv} → {targetEnv}
          </Typography>
          <Box mt={2}>
            {order.map((s, i) => {
              const sourceV = versions[s.name]?.[sourceEnv];
              const targetV = versions[s.name]?.[targetEnv];
              return (
                <div key={s.name} className={classes.planRow}>
                  <div className={classes.planIndex}>{i + 1}</div>
                  <div className={classes.serviceName}>{s.name}</div>
                  <div className={classes.versionArrow}>
                    <span>{targetV ?? '—'}</span>
                    <ArrowForwardIcon style={{ fontSize: 14 }} />
                    <span style={{ fontWeight: 600 }}>{sourceV ?? '—'}</span>
                  </div>
                  <Chip size="small" variant="outlined" label="promote" />
                </div>
              );
            })}
          </Box>
        </Paper>
      </Grid>

      <Grid item xs={12} md={4}>
        <Paper variant="outlined" className={classes.rail}>
          <div className={classes.railLabel}>
            <LinearScaleIcon style={{ fontSize: 14 }} />
            Rollout strategy
          </div>
          <Typography variant="subtitle2">Sequential</Typography>
          <Typography variant="caption" color="textSecondary">
            In dependency order. Halts on failure.
          </Typography>
        </Paper>

        <Box mt={2}>
          <Paper variant="outlined" className={classes.rail}>
            <div className={classes.railLabel}>
              <NotesIcon style={{ fontSize: 14 }} />
              Release notes
            </div>
            {hasNotes && bundleVersion ? (
              <>
                <Typography variant="subtitle2">
                  {notesMode === 'existing' ? 'Using bundle' : 'Published'} ·{' '}
                  <span style={{ fontFamily: 'monospace' }}>{bundleVersion}</span>
                </Typography>
                <Typography variant="caption" color="textSecondary">
                  Will become current after rollout
                </Typography>
              </>
            ) : (
              <Typography variant="body2" color="textSecondary">
                Skipped
              </Typography>
            )}
          </Paper>
        </Box>

        <Box mt={2}>
          <Paper variant="outlined" className={classes.rail}>
            <div className={classes.railLabel}>
              <PersonIcon style={{ fontSize: 14 }} />
              Owner
            </div>
            <Typography variant="subtitle2">{owner ?? 'You'}</Typography>
            <Typography variant="caption" color="textSecondary">
              No additional approvals required
            </Typography>
          </Paper>
        </Box>
      </Grid>
    </Grid>
  );
};
